'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { usePathname } from 'next/navigation';
import { Settings, Key, User, Bell, Shield, Eye, EyeOff, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const pathname = usePathname();
  const [geminiKey, setGeminiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [savedKey, setSavedKey] = useState(false);
  const [activeTab, setActiveTab] = useState<'profil' | 'api' | 'benachrichtigungen' | 'datenschutz'>('api');
  const [keyError, setKeyError] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);

  const handleSaveGeminiKey = () => {
    setKeyTouched(true);
    if (!geminiKey.trim()) {
      setKeyError('Bitte gib einen API-Key ein.');
      toast.error('Bitte gib einen gültigen API-Key ein.');
      return;
    }
    if (!geminiKey.startsWith('AIza')) {
      setKeyError('Ungültiger Key. Gemini API-Keys beginnen mit "AIza".');
      toast.error('Ungültiger Gemini API-Key. Keys beginnen mit "AIza".');
      return;
    }
    setKeyError('');
    // TODO: Backend — POST /api/settings/gemini-key with encrypted key
    setSavedKey(true);
    toast.success('Gemini API-Key gespeichert!', {
      description: 'Dein persönlicher Key wird für alle KI-Generierungen verwendet.',
    });
  };

  const handleRemoveKey = () => {
    setGeminiKey('');
    setSavedKey(false);
    setKeyError('');
    setKeyTouched(false);
    toast.success('API-Key entfernt. System-Key wird verwendet.');
  };

  const handleKeyChange = (value: string) => {
    setGeminiKey(value);
    if (keyTouched) {
      if (!value.trim()) {
        setKeyError('Bitte gib einen API-Key ein.');
      } else if (!value.startsWith('AIza')) {
        setKeyError('Ungültiger Key. Gemini API-Keys beginnen mit "AIza".');
      } else {
        setKeyError('');
      }
    }
  };

  const tabs = [
    { id: 'profil' as const, label: 'Profil', icon: <User size={14} /> },
    { id: 'api' as const, label: 'API & Keys', icon: <Key size={14} /> },
    { id: 'benachrichtigungen' as const, label: 'Benachrichtigungen', icon: <Bell size={14} /> },
    { id: 'datenschutz' as const, label: 'Datenschutz', icon: <Shield size={14} /> },
  ];

  return (
    <AppLayout currentPath={pathname}>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Settings size={20} className="text-accent" />
          <div>
            <h1 className="text-lg font-800 text-white uppercase tracking-widest">Einstellungen</h1>
            <p className="text-xs text-muted">Konfiguriere dein SEARCH4 DISTORTION Konto</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border pb-0" role="tablist" aria-label="Einstellungen Navigation">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-600 uppercase tracking-wider transition-all border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-accent text-accent' :'border-transparent text-muted hover:text-white'
              }`}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tab-panel-${tab.id}`}
              id={`tab-${tab.id}`}
            >
              <span aria-hidden="true">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* API Tab */}
        {activeTab === 'api' && (
          <div
            className="space-y-4"
            role="tabpanel"
            id="tab-panel-api"
            aria-labelledby="tab-api"
          >
            {/* BYOK Gemini Key */}
            <div className="punk-card p-5">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                <Key size={16} className="text-accent" aria-hidden="true" />
                <h2 className="text-sm font-700 text-white uppercase tracking-widest">Dein Gemini API Key</h2>
                <span className="ml-auto text-xs mono bg-accent/10 text-accent border border-accent/30 px-2 py-0.5 rounded" aria-label="Bring Your Own Key Funktion">BYOK</span>
              </div>

              <div className="mb-4 p-4 bg-surface border border-border rounded">
                <p className="text-xs text-muted leading-relaxed">
                  <span className="text-white font-600">Bring Your Own Key:</span> Wenn du deinen eigenen Google Gemini API-Key hinterlegst, werden alle KI-Generierungen über deinen Account abgerechnet. Ohne Key nutzt du das System-Kontingent.
                </p>
              </div>

              {savedKey ? (
                <div
                  className="flex items-center gap-3 p-4 bg-green-950/20 border border-green-800/30 rounded"
                  role="status"
                  aria-live="polite"
                  aria-label="API-Key ist aktiv gespeichert"
                >
                  <CheckCircle size={16} className="text-green-400 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <p className="text-xs font-600 text-green-400">API-Key aktiv</p>
                    <p className="text-xs text-muted mono">AIza••••••••••••••••••••••••••••••••••••</p>
                  </div>
                  <button
                    onClick={handleRemoveKey}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors mono focus:outline-none focus:ring-1 focus:ring-red-400 rounded px-1"
                    aria-label="Gespeicherten API-Key entfernen"
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="label-punk" htmlFor="gemini-key">Google Gemini API-Key</label>
                    <p className="text-xs text-muted-2 mb-2" id="gemini-key-desc">
                      Erhältlich im{' '}
                      <a
                        href="https://aistudio.google.com/app/apikey"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-white transition-colors inline-flex items-center gap-1"
                        aria-label="Google AI Studio öffnen (neues Fenster)"
                      >
                        Google AI Studio <ExternalLink size={10} aria-hidden="true" />
                      </a>
                    </p>
                    <div className="relative">
                      <input
                        id="gemini-key"
                        type={showKey ? 'text' : 'password'}
                        className={`input-punk pr-10 ${keyTouched && keyError ? 'border-red-500 focus:border-red-400' : keyTouched && !keyError && geminiKey ? 'border-green-600 focus:border-green-500' : ''}`}
                        placeholder="AIzaSy..."
                        value={geminiKey}
                        onChange={(e) => handleKeyChange(e.target.value)}
                        onBlur={() => {
                          setKeyTouched(true);
                          if (!geminiKey.trim()) setKeyError('Bitte gib einen API-Key ein.');
                          else if (!geminiKey.startsWith('AIza')) setKeyError('Ungültiger Key. Gemini API-Keys beginnen mit "AIza".');
                          else setKeyError('');
                        }}
                        aria-required="true"
                        aria-invalid={keyTouched && !!keyError}
                        aria-describedby={`gemini-key-desc${keyTouched && keyError ? ' gemini-key-error' : ''}${keyTouched && !keyError && geminiKey ? ' gemini-key-success' : ''}`}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKey(!showKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white transition-colors focus:outline-none focus:ring-1 focus:ring-accent rounded"
                        aria-label={showKey ? 'API-Key verbergen' : 'API-Key anzeigen'}
                        aria-pressed={showKey}
                      >
                        {showKey ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
                      </button>
                    </div>
                    {keyTouched && keyError && (
                      <p
                        id="gemini-key-error"
                        className="flex items-center gap-1 text-xs text-red-400 mt-1.5 mono"
                        role="alert"
                        aria-live="polite"
                      >
                        <AlertCircle size={11} aria-hidden="true" />
                        {keyError}
                      </p>
                    )}
                    {keyTouched && !keyError && geminiKey && (
                      <p
                        id="gemini-key-success"
                        className="flex items-center gap-1 text-xs text-green-500 mt-1.5 mono"
                        aria-live="polite"
                      >
                        <CheckCircle size={11} aria-hidden="true" />
                        Key-Format gültig. Klicke auf "Key speichern".
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveGeminiKey}
                      className="btn-primary text-xs focus:outline-none focus:ring-2 focus:ring-accent"
                      aria-label="Gemini API-Key speichern"
                    >
                      <Key size={12} aria-hidden="true" />
                      Key speichern
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Model Info */}
            <div className="punk-card p-5">
              <p className="section-label mb-3">KI-Modell-Konfiguration</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-border rounded bg-surface">
                  <div>
                    <p className="text-xs font-600 text-white">Gemini 2.5 Flash</p>
                    <p className="text-xs text-muted">Schnelle Entwürfe & Standard-Generierung</p>
                  </div>
                  <span
                    className="status-badge bg-green-950/30 text-green-400 border border-green-800/40"
                    role="status"
                    aria-label="Gemini 2.5 Flash Status: Aktiv"
                  >
                    Aktiv
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 border border-border rounded bg-surface">
                  <div>
                    <p className="text-xs font-600 text-white">Gemini 2.5 Pro</p>
                    <p className="text-xs text-muted">Tiefe Markenanalyse & Brand Vault</p>
                  </div>
                  <span
                    className="status-badge bg-green-950/30 text-green-400 border border-green-800/40"
                    role="status"
                    aria-label="Gemini 2.5 Pro Status: Aktiv"
                  >
                    Aktiv
                  </span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-accent/5 border border-accent/20 rounded">
                <p className="text-xs text-muted">
                  <span className="text-accent font-600">Hinweis:</span> Flash wird für schnelle Content-Generierung verwendet. Pro wird für Brand-DNA-Analysen und tiefe Kontextverarbeitung eingesetzt.
                </p>
              </div>
            </div>

            {/* Credits */}
            <div className="punk-card p-5">
              <p className="section-label mb-3">Guthaben & Nutzung</p>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-700 text-white">System-Kontingent</p>
                  <p className="text-xs text-muted">Verbleibende Generierungen diesen Monat</p>
                </div>
                <span className="mono text-2xl font-700 text-accent">47</span>
              </div>
              <div className="h-2 bg-surface-2 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-accent rounded-full" style={{ width: '47%' }} />
              </div>
              <div className="flex justify-between text-xs mono text-muted mb-4">
                <span>47 / 100 verbleibend</span>
                <span>Reset: 01.05.2026</span>
              </div>
              <button className="btn-primary text-xs w-full justify-center">
                Guthaben aufladen
              </button>
            </div>
          </div>
        )}

        {/* Profil Tab */}
        {activeTab === 'profil' && (
          <div
            className="punk-card p-5"
            role="tabpanel"
            id="tab-panel-profil"
            aria-labelledby="tab-profil"
          >
            <p className="section-label mb-4">Profil-Einstellungen</p>
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
              <div className="w-16 h-16 rounded bg-accent/20 border border-accent/40 flex items-center justify-center">
                <User size={24} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-700 text-white">Klaus Berger</p>
                <p className="text-xs text-muted mono">CEO · Berger GmbH</p>
                <button className="text-xs text-accent hover:text-white transition-colors mt-1">
                  Profilbild ändern
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-punk" htmlFor="vorname">Vorname</label>
                  <input id="vorname" className="input-punk" defaultValue="Klaus" />
                </div>
                <div>
                  <label className="label-punk" htmlFor="nachname">Nachname</label>
                  <input id="nachname" className="input-punk" defaultValue="Berger" />
                </div>
              </div>
              <div>
                <label className="label-punk" htmlFor="unternehmen">Unternehmen</label>
                <input id="unternehmen" className="input-punk" defaultValue="Berger GmbH" />
              </div>
              <div>
                <label className="label-punk" htmlFor="email-settings">E-Mail</label>
                <input id="email-settings" type="email" className="input-punk" defaultValue="k.berger@berger-gmbh.de" />
              </div>
              <div className="flex justify-end">
                <button onClick={() => toast.success('Profil gespeichert!')} className="btn-primary text-xs">
                  Profil speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Benachrichtigungen Tab */}
        {activeTab === 'benachrichtigungen' && (
          <div className="punk-card p-5">
            <p className="section-label mb-4">Benachrichtigungs-Einstellungen</p>
            <div className="space-y-3">
              {[
                { label: 'Generierung abgeschlossen', desc: 'Wenn dein 14-Tage-Kalender fertig ist' },
                { label: 'Export bereit', desc: 'Wenn ein ZIP-Paket zum Download bereit ist' },
                { label: 'Google Drive Sync', desc: 'Synchronisierungsstatus-Updates' },
                { label: 'Guthaben niedrig', desc: 'Wenn weniger als 10 Generierungen verbleiben' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-border rounded bg-surface">
                  <div>
                    <p className="text-xs font-600 text-white">{item.label}</p>
                    <p className="text-xs text-muted">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-9 h-5 bg-surface-2 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent border border-border" />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Datenschutz Tab */}
        {activeTab === 'datenschutz' && (
          <div className="space-y-4">
            <div className="punk-card p-5">
              <p className="section-label mb-4">Datenschutz & Sicherheit</p>
              <div className="space-y-3">
                <div className="p-4 bg-surface border border-border rounded">
                  <p className="text-xs font-600 text-white mb-1">Datenspeicherung</p>
                  <p className="text-xs text-muted leading-relaxed">Deine Brand-DNA und generierten Inhalte werden verschlüsselt gespeichert. API-Keys werden niemals im Klartext gespeichert.</p>
                </div>
                <div className="p-4 bg-surface border border-border rounded">
                  <p className="text-xs font-600 text-white mb-1">KI-Verarbeitung</p>
                  <p className="text-xs text-muted leading-relaxed">Inhalte werden zur Generierung an Google Gemini übertragen. Google verarbeitet diese gemäß ihrer Datenschutzrichtlinien.</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <button onClick={() => toast.error('Konto-Löschung angefordert — Bestätigungs-E-Mail wird gesendet.')} className="text-xs text-red-400 hover:text-red-300 transition-colors">
                  Konto löschen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
